// Chakra imports
import {
  Portal,
  Box,
  useDisclosure,
  VStack,
  Heading,
  Flex,
} from '@chakra-ui/react';

// Layout components
import { SidebarContext } from '../../contexts/SidebarContext';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../routes';
import Footer from '../../components/footer/FooterAdmin';
import Sidebar, { SidebarResponsive } from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/NavbarAdmin';
import { getUserByFirebaseUid } from 'api/api';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { useTranslation } from 'i18n/TranslationProvider';
import ForbiddenPage from 'domains/shared/pages/errors/ForbiddenPage';
import { getPostAuthRedirectPath } from 'domains/auth/utils/postAuthRedirect';

export default function Dashboard(props) {
  const { ...rest } = props;
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const { uid, loading: authLoading } = useFirebaseAuth();
  const [forceRedirect, setForceRedirect] = useState(null);

  const location = useLocation();
  const sidebarDrawer = useDisclosure();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (uid) {
        try {
          const user = await getUserByFirebaseUid(uid);
          const redirectPath = getPostAuthRedirectPath(user);
          if (redirectPath !== '/admin/dashboard' && redirectPath !== '/dashboard/partner' && redirectPath !== '/dashboard/client') {
            setForceRedirect(redirectPath);
            return;
          }
          setUserRole(user.role);
        } catch (error) {
          console.error('Failed to fetch user role:', error);
          setForceRedirect('/auth/sign-in');
        }
      } else if (!authLoading) {
        setForceRedirect('/auth/sign-in');
      }
    };
    if (!authLoading) fetchUserRole();
  }, [uid, authLoading]);

  const filteredRoutes = userRole
    ? routes.filter(
        (route) =>
          route.layout === '/' && route.roles && route.roles.includes(userRole)
      )
    : [];

  const redirectPath =
    filteredRoutes.length > 0 ? filteredRoutes[0].path : null;

  const normalizePath = (path) => {
    if (!path) return '/';
    if (path === '/') return '/';
    return path.replace(/\/+$/, '');
  };

  const matchesRoutePath = (routePath, currentPath) => {
    const normalizedRoute = normalizePath(routePath);
    const normalizedCurrent = normalizePath(currentPath);

    const routeParts = normalizedRoute.split('/');
    const currentParts = normalizedCurrent.split('/');

    if (routeParts.length !== currentParts.length) return false;

    return routeParts.every((part, index) => part.startsWith(':') || part === currentParts[index]);
  };

  const isUnauthorizedPath = !!(
    userRole &&
    routes.find((route) => {
      if (!route.roles || route.roles.includes(userRole)) return false;

      const candidatePaths =
        route.layout && route.layout !== '/'
          ? [route.path, `${route.layout}${route.path}`]
          : [route.path];

      return candidatePaths.some((candidate) =>
        matchesRoutePath(candidate, location.pathname),
      );
    })
  );

  const getRoute = () => window.location.pathname !== '/full-screen-maps';

  const getActiveRoute = (routes) => {
    const pathname = location.pathname;
    if (pathname === '/create-booking') {
      return (
        <VStack align="flex-start" spacing={1}>
          <Heading as="h1" size="lg" color="black" fontWeight="bold">
            {t('Book your transport')}
          </Heading>
          <Heading as="h2" size="sm" color="gray.500" fontWeight="normal">
            {t('fast and easy')}
          </Heading>
        </VStack>
      );
    }
    let active = t('My Profile');
    for (let i = 0; i < routes.length; i++) {
      const r = routes[i];
      if (r.collapse) {
        const nested = getActiveRoute(r.items);
        if (nested !== 'Default Brand Text') return nested;
      } else if (r.category) {
        const nested = getActiveRoute(r.items);
        if (nested !== 'Default Brand Text') return nested;
      } else {
        const fullPath = r.layout === '/' ? r.path : r.layout + r.path;
        if (fullPath.includes('/:')) {
          const base = fullPath.split('/:')[0];
          if (pathname.startsWith(base)) {
            return t(r.name || 'Default Brand Text');
          }
        } else if (pathname === fullPath) {
          return t(r.name || 'Default Brand Text');
        }
      }
    }
    return active;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse || routes[i].category) {
        const nestedNavbar = getActiveNavbar(routes[i].items);
        if (nestedNavbar !== activeNavbar) return nestedNavbar;
      } else if (
        window.location.href.includes(routes[i].layout + routes[i].path)
      ) {
        return routes[i].secondary;
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse || routes[i].category) {
        const nestedText = getActiveNavbarText(routes[i].items);
        if (nestedText !== activeNavbar) return nestedText;
      } else if (
        window.location.href.includes(routes[i].layout + routes[i].path)
      ) {
        return routes[i].messageNavbar ? t(routes[i].messageNavbar) : routes[i].messageNavbar;
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) => {
    return routes.flatMap((route, key) => {
      if (!route.roles || route.roles.includes(userRole)) {
        if (route.component) {
          return (
            <Route path={route.path} element={route.component} key={key} />
          );
        }
      }
      if (route.collapse || route.category) {
        return getRoutes(route.items || []);
      }
      return [];
    });
  };

  document.documentElement.dir = 'ltr';
  const SIDEBAR_WIDTH = 240;

  if (forceRedirect) {
    return <Navigate to={forceRedirect} replace />;
  }

  if (authLoading || (!userRole && !forceRedirect)) {
    return null; // Prevent flashing the UI before userrole is checked
  }

  return (
    <Box>
      <SidebarContext.Provider
        value={{ toggleSidebar, setToggleSidebar, openMobileSidebar: sidebarDrawer.onOpen }}
      >
        <Flex>
          <Sidebar
            routes={filteredRoutes}
            display={{ base: 'none', xl: 'block' }}
            width={SIDEBAR_WIDTH}
            {...rest}
          />
          <SidebarResponsive
            routes={filteredRoutes}
            display={{ base: 'block', xl: 'none' }}
            width={SIDEBAR_WIDTH}
            isOpen={sidebarDrawer.isOpen}
            onOpen={sidebarDrawer.onOpen}
            onClose={sidebarDrawer.onClose}
            showTrigger={false}
            {...rest}
          />
          <Box
            ml={{ base: 0, xl: `${SIDEBAR_WIDTH}px` }}
            minHeight="100vh"
            height="100%"
            overflow="auto"
            position="relative"
            maxHeight="100%"
            w={{ base: '100%', xl: `calc(100% - ${SIDEBAR_WIDTH}px)` }}
            maxWidth={{ base: '100%', xl: `calc(100% - ${SIDEBAR_WIDTH}px)` }}
            transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
            transitionDuration=".2s, .2s, .35s"
            transitionProperty="top, bottom, width"
            transitionTimingFunction="linear, linear, ease"
            style={{ '--sidebar-width': `${SIDEBAR_WIDTH}px` }}
          >
            <Portal>
              <Navbar
                onOpen={sidebarDrawer.onOpen}
                logoText="LogiLift"
                brandText={getActiveRoute(
                  filteredRoutes.length ? filteredRoutes : routes
                )}
                secondary={getActiveNavbar(
                  filteredRoutes.length ? filteredRoutes : routes
                )}
                message={getActiveNavbarText(
                  filteredRoutes.length ? filteredRoutes : routes
                )}
                fixed={fixed}
                sidebarWidth={SIDEBAR_WIDTH}
                {...rest}
              />
            </Portal>

            {getRoute() && (
              <>
                {isUnauthorizedPath ? (
                  <ForbiddenPage homePath={redirectPath || '/'} />
                ) : (
                  <Box
                    mx="auto"
                    p={{ base: '20px', md: '30px' }}
                    pe="20px"
                    minH="100vh"
                    pt="50px"
                  >
                    <Routes>
                      {filteredRoutes.length > 0 && getRoutes(filteredRoutes)}
                      {redirectPath && (
                        <Route
                          path="/"
                          element={<Navigate to={redirectPath} replace />}
                        />
                      )}
                      <Route
                        path="*"
                        element={<Navigate to={redirectPath || '/'} replace />}
                      />
                    </Routes>
                  </Box>
                )}
              </>
            )}
            <Footer />
          </Box>
        </Flex>
      </SidebarContext.Provider>
    </Box>
  );
}
